import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, Box, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { Transition } from 'react-transition-group';
import CloseIcon from '@mui/icons-material/Close';
import { greenTheme } from '../helpers/style';

const primaryColor = process.env.REACT_APP_SERVER === 'ATS' ? '#0973b9' : greenTheme.primary;
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { toast } from 'react-toastify';
import { menu, helpdesk, crm, telecomunication } from '../translate';
import Api from '../Api';
import '../pages/new_operator/helpdesk.css';

const CATEGORY_KEYS = [
  { value: 'employees', menuKey: 'worker', source: 'menu' },
  { value: 'groups', menuKey: 'group', source: 'menu' },
  { value: 'ivrs', menuKey: 'ivr', source: 'menu' },
  { value: 'services', menuKey: 'shortF', source: 'menu' },
  { value: 'did', menuKey: 'did', source: 'menu' },
  { value: 'file', menuKey: 'file', source: 'menu' },
  { value: 'autoCall', menuKey: 'autoCall', source: 'menu' },
  { value: 'crm', menuKey: 'crm', source: 'crm' },
  { value: 'telegram', menuKey: 'telegram_title', source: 'telecomunication' },
  { value: 'whatsapp', menuKey: 'whatsapp_title', source: 'telecomunication' },
  { value: 'operatorReport', menuKey: 'operatorReport', source: 'menu' },
  { value: 'provide', menuKey: 'provide', source: 'menu' },
  { value: 'other', menuKey: 'helpdesk_other', source: 'helpdesk' },
];

const SOURCE_MAP = { menu, helpdesk, crm, telecomunication };

const MAX_MENU_CHARS = 24;
function MenuText({ text }) {
  const str = String(text || '');
  if (str.length <= MAX_MENU_CHARS) return str;
  return (
    <Tooltip title={str} placement="top">
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }}>
        {str.slice(0, MAX_MENU_CHARS)}…
      </span>
    </Tooltip>
  );
}

const transitionStyles = {
  exited: { top: '100vh', visibility: 'hidden' },
  entering: { top: 0, transition: 'top 400ms ease-out' },
  entered: { top: 0 },
  exiting: { top: '100vh', transition: 'top 400ms ease-in' },
};
function TopSlideTransition({ in: inProp, timeout = 400, children, ...rest }) {
  const childRef = React.useRef(null);
  const handleEnter = () => {
    const node = childRef.current;
    if (node) {
      node.style.position = 'fixed';
      node.style.top = '100vh';
      node.style.transition = 'none';
      node.scrollTop; // reflow
    }
  };
  const handleEntering = () => {
    const node = childRef.current;
    if (node) {
      node.style.transition = `top ${timeout}ms ease-out`;
      node.style.top = '0';
    }
  };
  const handleExiting = () => {
    const node = childRef.current;
    if (node) {
      node.style.transition = `top ${timeout}ms ease-in`;
      node.style.top = '100vh';
    }
  };
  const child = React.Children.only(children);
  const handleRef = (el) => {
    childRef.current = el;
    if (child.ref) {
      if (typeof child.ref === 'function') child.ref(el);
      else child.ref.current = el;
    }
  };
  return (
    <Transition
      in={inProp}
      timeout={timeout}
      nodeRef={childRef}
      onEnter={handleEnter}
      onEntering={handleEntering}
      onExiting={handleExiting}
      {...rest}
    >
      {(state) => React.cloneElement(child, {
        ref: handleRef,
        style: {
          ...child.props?.style,
          position: 'fixed',
          ...transitionStyles[state],
        },
      })}
    </Transition>
  );
}

function getCategoryOptions(lang) {
  const langIndex = Math.min(Math.max(1, +(lang || 1)), 3);
  return CATEGORY_KEYS.map(({ value, menuKey, source }) => {
    let label;
    const dict = SOURCE_MAP[source];
    if (dict && dict[menuKey]) label = dict[menuKey][langIndex];
    else label = value;
    return { value, label };
  });
}

function getCategoryLabel(value, lang) {
  const opts = getCategoryOptions(lang);
  const found = opts.find((o) => o.value === value);
  return found ? found.label : value;
}

function ensureTranslations(val) {
  if (Array.isArray(val) && val.length >= 4) return val;
  const s = typeof val === 'string' ? val : '';
  return ['', s, s, s];
}

function normalizeItem(item) {
  if (!item) return item;
  return {
    ...item,
    title: ensureTranslations(item.title),
    slug: ensureTranslations(item.slug),
    content: ensureTranslations(item.content),
    excerpt: ensureTranslations(item.excerpt),
  };
}

function getItemText(item, field, lang) {
  if (!item) return '';
  const val = item[field];
  if (Array.isArray(val)) {
    const idx = Math.min(3, Math.max(1, +(lang || 1)));
    return (val[idx] ?? val[1] ?? '').trim();
  }
  return typeof val === 'string' ? val : '';
}

export default function HelpDeskPreviewDrawer({ open, onClose, items: itemsProp, item: itemProp, onSelectArticle, lang: langProp }) {
  const isControlled = itemsProp != null;
  const [itemsState, setItemsState] = useState([]);
  const [selectedItemState, setSelectedItemState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [ratedArticleIds, setRatedArticleIds] = useState(() => new Set());

  const items = isControlled ? (itemsProp || []).map(normalizeItem) : itemsState;
  const selectedItem = isControlled ? (itemProp ? normalizeItem(itemProp) : null) : selectedItemState;

  const lang = langProp != null ? Math.min(3, Math.max(1, +langProp)) : Math.min(3, Math.max(1, +(localStorage.getItem('atsLang') || 1)));
  const categoryOptions = useMemo(() => getCategoryOptions(lang), [lang]);

  useEffect(() => {
    if (!open || isControlled) return;
    let cancelled = false;
    setLoading(true);
    setSelectedItemState(null);
    Api.getHelpDeskList()
      .then(({ data }) => {
        if (cancelled) return;
        const list = (data?.items || []).map(normalizeItem).filter((it) => it.status !== 'draft');
        setItemsState(list);
        setSelectedItemState(list.length ? list[0] : null);
      })
      .catch(() => {
        if (!cancelled) setItemsState([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, isControlled]);

  const byCategory = useMemo(() => {
    const map = new Map();
    categoryOptions.forEach((c) => map.set(c.value, { label: c.label, articles: [] }));
    items.forEach((it) => {
      const slot = map.get(it.category);
      if (slot) slot.articles.push(it);
    });
    return categoryOptions
      .map((c) => ({ value: c.value, label: c.label, articles: (map.get(c.value) || {}).articles || [] }))
      .filter((g) => g.articles.length > 0);
  }, [items, categoryOptions]);

  const didExpandFirst = React.useRef(false);
  useEffect(() => {
    if (byCategory.length > 0 && !didExpandFirst.current) {
      didExpandFirst.current = true;
      const firstGroup = byCategory[0];
      setExpandedCategories({ [firstGroup.value]: true });
      if (!isControlled && firstGroup.articles.length > 0) {
        setSelectedItemState(firstGroup.articles[0]);
      }
    }
  }, [byCategory, isControlled]);
  useEffect(() => {
    if (!open) didExpandFirst.current = false;
  }, [open]);

  const toggleCategory = (value) => {
    setExpandedCategories((prev) => ({ ...prev, [value]: !prev[value] }));
  };

  const categoryLabel = selectedItem ? getCategoryLabel(selectedItem.category, lang) : '';
  const itemTitle = selectedItem ? getItemText(selectedItem, 'title', lang) : '';
  const itemContent = selectedItem ? getItemText(selectedItem, 'content', lang) : '';
  const itemExcerpt = selectedItem ? getItemText(selectedItem, 'excerpt', lang) : '';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      TransitionComponent={TopSlideTransition}
      transitionDuration={400}
      ModalProps={{
        BackdropProps: { sx: { bgcolor: 'rgba(0,0,0,0.4)' } },
      }}
      sx={{
        '& .MuiDrawer-paper': {
          position: 'fixed',
          right: 0,
          width: { xs: '100%', sm: 480, md: 970 },
          maxWidth: 970,
          height: 'calc(100vh - 40px)',
          marginTop: '40px',
          marginRight: '45px',
          borderRadius: '12px',
          overflow: 'visible',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          borderRight: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', height: '100%' }}>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            left: -56,
            top: 44,
            zIndex: 1,
            width: 56,
            height: 40,
            borderRadius: '12px 0 0 12px',
            backgroundColor: primaryColor,
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': { backgroundColor: primaryColor, opacity: 0.9, color: '#fff' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box className="hd-preview-drawer" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#fff', borderRadius: '12px 12px 0 0' }}>
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: '#111827', fontSize: '1rem' }}>{helpdesk.help_center[lang] || 'Help Center'}</span>
          </div>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}>
          {/* Grid: one row, height = max(aside content, main); both columns stretch to that height */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '280px minmax(0, 1fr)',
              alignItems: 'stretch',
              width: '100%',
              minHeight: 'min-content',
            }}
          >
            <aside
              className="hd-side-menu hd-menu-accordion"
              style={{
                backgroundColor: '#f5f5f5',
                minWidth: 0,
                height: '100%',
              }}
            >
              <div style={{ padding: '1rem 1rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>
                {helpdesk.related_articles[lang] || 'Related Articles'}
              </div>
              {loading ? (
                <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} sx={{ color: '#7ebc0a' }} />
                </Box>
              ) : (
                byCategory.map((group) => {
                  const isOpen = expandedCategories[group.value] === true;
                  return (
                    <div key={group.value} className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                          type="button"
                          className="accordion-button"
                          onClick={() => toggleCategory(group.value)}
                          style={{ justifyContent: 'space-between' }}
                        >
                          <span><MenuText text={group.label} /></span>
                          <span style={{ fontSize: '0.75rem' }}>{isOpen ? '▼' : '▶'}</span>
                        </button>
                      </h2>
                      {isOpen && (
                        <div className="accordion-collapse collapse show">
                          {group.articles.map((art) => (
                            <button
                              key={art.id}
                              type="button"
                              className={`accordion-button accordion-link ${selectedItem?.id === art.id ? 'active' : ''}`}
                              onClick={() => (onSelectArticle ? onSelectArticle(art) : setSelectedItemState(art))}
                            >
                              <span />
                              <MenuText text={getItemText(art, 'title', lang)} />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </aside>
            <main style={{ padding: '1.5rem 2rem', minWidth: 0, height: '100%', boxSizing: 'border-box' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <CircularProgress sx={{ color: '#7ebc0a' }} />
                </Box>
              ) : !selectedItem ? (
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  {items.length === 0 ? (helpdesk.no_articles_found[lang] || 'No articles found.') : (helpdesk.related_articles[lang] || 'Select an article from the left.')}
                </p>
              ) : (
                <>
                  <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>{itemTitle}</h1>
                  {categoryLabel && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{categoryLabel}</span>
                    </div>
                  )}
                  {itemExcerpt && (
                    <p style={{ fontStyle: 'italic', color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>{itemExcerpt}</p>
                  )}
                  <div
                    className="hd-preview-content"
                    dangerouslySetInnerHTML={{ __html: itemContent }}
                    style={{ fontSize: '0.9375rem', lineHeight: 1.6, color: '#374151' }}
                  />
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    {ratedArticleIds.has(selectedItem.id) ? (
                      <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280' }}>
                        {helpdesk.thank_you_rate?.[lang] || 'Thank you for your feedback.'}
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                          {helpdesk.was_helpful[lang] || 'Was this helpful?'}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            className="hd-btn hd-btn-secondary"
                            style={{ padding: '0.5rem' }}
                            onClick={() => {
                              setRatedArticleIds((prev) => new Set(prev).add(selectedItem.id));
                              toast.success(helpdesk.thank_you_rate?.[lang] || 'Thank you for your feedback.');
                            }}
                          >
                            <ThumbUpIcon sx={{ fontSize: 18 }} />
                          </button>
                          <button
                            type="button"
                            className="hd-btn hd-btn-secondary"
                            style={{ padding: '0.5rem' }}
                            onClick={() => {
                              setRatedArticleIds((prev) => new Set(prev).add(selectedItem.id));
                              toast.success(helpdesk.thank_you_rate?.[lang] || 'Thank you for your feedback.');
                            }}
                          >
                            <ThumbDownIcon sx={{ fontSize: 18 }} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </main>
          </div>
        </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
